import { DefaultTheme } from 'styled-components'
import { omit } from 'lodash-es'
import { FixMeAny } from 'types/global'
import { DropdownStyleProps } from './types'

type VariantStyleProps = DropdownStyleProps & {
  theme: DefaultTheme
}

const baseStyle = ({ error, theme: { colors } }: VariantStyleProps) => ({
  container: (styles: FixMeAny, { isFocused }: FixMeAny) => ({
    ...styles,
    boxShadow: isFocused ? `0 0 3px 3px ${colors.gray1}` : 'none',
    width: '100%',
  }),
  control: (styles: FixMeAny, { menuIsOpen, isDisabled, isMulti, minHeight }: FixMeAny) => ({
    ...styles,
    background: isDisabled ? colors.gray1 : colors.white,
    border: `1px solid ${error ? colors.red : menuIsOpen ? colors.blue1 : colors.gray3}`,
    borderRadius: '4px',
    boxShadow: menuIsOpen ? `0 0 0 4px ${colors.gray2}` : 'none',
    color: error ? colors.red : colors.defaultText,
    cursor: 'pointer',
    minHeight: minHeight ? minHeight : '48px',
    height: isMulti ? undefined : '48px',
    padding: '0 16px',
    '&:hover': {
      borderColor: colors.blue1,
    },
  }),
  dropdownIndicator: (styles: FixMeAny) => ({
    ...styles,
    padding: 0,
  }),
  group: (styles: FixMeAny) => ({
    ...styles,
    padding: 0,
  }),
  groupHeading: (styles: FixMeAny) => ({
    ...styles,
    borderBottom: `1px solid ${colors.gray3}`,
    color: colors.gray8,
    backgroundColor: colors.gray1,
    fontFamily: 'montserrat',
    fontWeight: 'medium',
    padding: `4px 4px 4px 16px`,
  }),
  indicatorsContainer: (styles: FixMeAny) => ({
    ...styles,
    padding: 0,
  }),
  placeholder: (styles: FixMeAny, { isDisabled }: FixMeAny) => ({
    ...styles,
    color: error ? colors.red : isDisabled ? colors.gray4 : colors.gray5,
    fontSize: '14px',
  }),
  singleValue: (styles: FixMeAny, { data, isDisabled }: FixMeAny) => {
    const getColor = () => {
      if (data.labelColor) {
        return data.labelColor
      }

      if (error) {
        return colors.red
      }

      if (isDisabled) {
        return colors.gray4
      }

      if (typeof data.value === 'string' && data.value?.trim() === '') {
        return colors.disabledText
      }

      return colors.defaultText
    }

    return {
      ...styles,
      color: getColor(),
      fontSize: '14px',
      padding: '4px 0',
    }
  },
  valueContainer: (styles: FixMeAny, { isMulti, isMaxLimitReached }: FixMeAny) => ({
    ...styles,
    padding: isMulti ? '0.2rem' : 0,
    maxInlineSize: isMaxLimitReached ? '90%' : '100%',
    gap: '0.2rem',
  }),
  menu: (styles: FixMeAny) => ({
    ...styles,
    background: 'transparent',
    borderRadius: '4px',
    left: '1px',
    width: 'calc(100% - 2px)',
    margin: '3px auto',
  }),
  menuList: (styles: FixMeAny) => ({
    ...styles,
    background: colors.white,
    padding: 0,
  }),
  option: (styles: FixMeAny, { data, isDisabled, isFocused, isMulti, isSelected }: FixMeAny) => ({
    ...styles,
    alignItems: 'center',
    background: isFocused ? colors.gray2 : colors.white,
    borderBottom: `1px solid ${colors.gray3}`,
    borderRadius: undefined,
    cursor: 'pointer',
    display: 'flex',
    fontSize: '14px',
    opacity: isDisabled ? '0.5' : '1',
    outline: '0 solid transparent',
    padding: data?.indention
      ? `${isMulti ? '0 0 0' : '16px 16px 16px'} ${data.indention * 17 + 16}px`
      : data.node
      ? '8px 16px'
      : isMulti || data?.type === 'action'
      ? 0
      : '16px',
    textAlign: 'left',
    '&:hover': {
      background: isSelected || data?.type === 'action' ? colors.blue5 : colors.gray2,
    },
    width: isMulti ? '100%' : undefined,
  }),
})

/**
 * Dropdown with normal placeholder
 */
const simple = (props: VariantStyleProps) => ({
  ...baseStyle({ inline: false, ...props }),
})

/**
 * Dropdown with floating label
 */
const labeled = (props: VariantStyleProps) => ({
  ...baseStyle({ inline: false, ...props }),
})

/**
 * Dropdown with action look
 */
const action = (props: VariantStyleProps) => ({
  ...baseStyle({ inline: false, ...props }),
  placeholder: (styles: FixMeAny) => ({
    ...styles,
    fontSize: '14px',
    paddingLeft: '6px',
  }),
  singleValue: (styles: FixMeAny) => ({
    ...styles,
    color: props.theme.colors.blue1,
    fontSize: '14px',
    paddingLeft: '6px',
  }),
  control: (styles: FixMeAny, { menuIsOpen, isDisabled }: FixMeAny) => ({
    ...styles,
    background: isDisabled ? props.theme.colors.gray1 : props.theme.colors.white,
    border: `1px solid ${props.theme.colors.gray4}`,
    borderRadius: '4px',
    boxShadow: menuIsOpen ? `0 0 0 4px ${props.theme.colors.gray2}` : 'none',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'none',
    },
  }),
  indicatorsContainer: (styles: FixMeAny) => ({
    ...styles,
    borderLeft: `1px solid ${props.theme.colors.gray4}`,
    padding: 0,
  }),
  dropdownIndicator: (styles: FixMeAny) => ({
    ...styles,
    padding: '0 8px',
  }),
})

/**
 * Dropdown with no box or label
 */
const boxless = (props: VariantStyleProps) => ({
  ...baseStyle({ inline: true, ...props }),
  container: (styles: FixMeAny, _otherProps: FixMeAny) => ({
    ...styles,
    boxShadow: 'none',
    margin: 0,
    width:
      props.width === 'auto' && props.inline
        ? '250px'
        : props.width === 'auto'
        ? 'calc(100% - 16px)'
        : `${props.width}px`,
  }),
  control: (styles: FixMeAny, _otherProps: FixMeAny) => ({
    ...styles,
    background: 'none',
    border: 'none',
    boxShadow: 'none',
  }),
  placeholder: (styles: FixMeAny) => ({
    ...styles,
    color: props.error ? props.theme.colors.red : props.theme.colors.gray5,
    fontSize: '14px',
  }),
  singleValue: (styles: FixMeAny) => ({
    ...omit(styles, ['maxWidth', 'position', 'transform']),
    fontSize: '14px',
    padding: 0,
  }),
  menu: (styles: FixMeAny) => ({
    ...styles,
    marginTop: '12px',
    whiteSpace: 'nowrap',
    left: 0,
  }),
})

export default {
  simple,
  labeled,
  action,
  'single-boxless': boxless,
}
